import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, IconButton } from '../../../ui/v2';
import { Avatar, Callout, EmptyState, Reason } from '../../../ui/v2';
import { Input, Radio, Select, Textarea } from '../../../ui/v2';
import { SaveBar } from '../../../ui/v2';
import { ConfirmDialog, Dialog } from '../../../ui/v2';
import { Tag } from '../../../ui/v2';
import { IconPause, IconPlay, IconSearch } from '../../../ui/v2';
import {
  useStoreActions,
  useStoreInfoQuery,
  useVoiceActions,
  useVoiceSelectionQuery,
  useVoicesQuery,
} from './data';
import {
  type VoiceOption,
  type VoiceSelection,
  VOICE_STATUS_NOTE,
  defaultCallIntroSentence,
  voiceLabel,
  voiceSearchText,
} from './model';
import {
  Block,
  BlockSkeleton,
  QueryGate,
  SavedNote,
  SectionBody,
  SectionHead,
  useSavedAt,
} from './shared';

/**
 * Voice & greeting.
 *
 * - Agent voice: the current voice as one row (play · avatar · name · traits ·
 *   Change). Choosing happens in the only Dialog of the section and saves
 *   from the Dialog (one PATCH), because a voice applies to every phone line
 *   the moment it's saved — it isn't part of the page's draft.
 * - Call intro: default vs custom as radios; the custom text is a textarea.
 *   Saved through the SaveBar. Saving a custom intro asks for confirmation
 *   because every caller hears it first (production behaviour, kept).
 */
const VoiceSection: React.FC = () => {
  const intro = useCallIntro();
  return (
    <SectionBody measure="voice" footer={intro.footer}>
      <SectionHead title="Voice & greeting" />
      <div className="mt-6">
        <AgentVoiceBlock />
        {intro.block}
      </div>
    </SectionBody>
  );
};

// ---------------------------------------------------------------------------
// Audio preview — one element, one thing playing at a time
// ---------------------------------------------------------------------------

function useAudioPreview() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    setPlayingId(null);
  }, []);

  useEffect(() => stop, [stop]);

  const toggle = useCallback(
    (voice: VoiceOption) => {
      if (!voice.preview_audio_url) return;
      if (playingId === voice.voice_id) {
        stop();
        return;
      }
      stop();
      setPlayingId(voice.voice_id);
      const url = voice.preview_audio_url;
      if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.preload = 'none';
        audioRef.current.onended = () => setPlayingId(null);
      }
      audioRef.current.src = url;
      void audioRef.current.play().catch(() => setPlayingId(null));
    },
    [playingId, stop],
  );

  return { playingId, toggle, stop };
}

const PlayButton: React.FC<{
  voice: VoiceOption;
  playing: boolean;
  onToggle: () => void;
  size?: 'sm' | 'md';
}> = ({ voice, playing, onToggle, size = 'sm' }) => (
  <IconButton
    size={size}
    variant="secondary"
    label={
      playing
        ? `Stop ${voice.alias} sample`
        : voice.preview_audio_url
          ? `Play ${voice.alias} sample`
          : `No sample for ${voice.alias}`
    }
    disabled={!voice.preview_audio_url}
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
    className={clsx(
      'text-v2-accent',
      playing ? 'border-v2-accent bg-v2-accent/10' : 'border-v2-accent/40',
    )}
  >
    {playing ? <IconPause size={12} /> : <IconPlay size={12} />}
  </IconButton>
);

const VoiceAvatar: React.FC<{
  voice: VoiceOption | null;
  size?: 'sm' | 'md';
}> = ({ voice, size = 'sm' }) =>
  voice?.avatar_url ? (
    <img
      src={voice.avatar_url}
      alt=""
      className={clsx(
        'shrink-0 rounded-full object-cover',
        size === 'md' ? 'h-8 w-8' : 'h-6 w-6',
      )}
    />
  ) : (
    <Avatar name={voice?.alias} size={size} arthur={!voice} />
  );

// ---------------------------------------------------------------------------
// Agent voice
// ---------------------------------------------------------------------------

/** The current voice with Change; also on Test your agent. */
export const AgentVoiceBlock: React.FC = () => {
  const selection = useVoiceSelectionQuery();
  const catalog = useVoicesQuery(selection.data?.catalog_available !== false);
  const voices = catalog.data ?? [];
  const preview = useAudioPreview();
  const [open, setOpen] = useState(false);
  const [savedAt, markSaved] = useSavedAt();
  const [savedText, setSavedText] = useState('Saved.');

  return (
    <Block
      first
      title="Agent voice"
      description="Callers hear this voice on every active phone line. Play a sample before you save."
    >
      <QueryGate query={selection} skeleton={<BlockSkeleton rows={2} />}>
        {(sel) => {
          const displayId =
            sel.voice_id ||
            (sel.status === 'default' ? sel.agent_voice_id : null);
          const current = voices.find((v) => v.voice_id === displayId) ?? null;
          const label = sel.alias
            ? voiceLabel({
                alias: sel.alias,
                source_name: sel.source_name || undefined,
              })
            : sel.status === 'default'
              ? 'Agent default'
              : sel.status === 'mixed'
                ? 'Multiple voices'
                : sel.status === 'unrecognized'
                  ? 'Voice outside this catalog'
                  : 'No voice set';
          const canEdit = Boolean(sel.can_edit) && sel.status !== 'unavailable';
          const note = VOICE_STATUS_NOTE[sel.status];
          return (
            <>
              {note && (
                <Callout tone="warning" className="mb-3 max-w-[640px]">
                  {note}
                </Callout>
              )}
              <div className="flex h-11 max-w-[640px] items-center gap-3">
                {current ? (
                  <PlayButton
                    voice={current}
                    playing={preview.playingId === current.voice_id}
                    onToggle={() => preview.toggle(current)}
                    size="md"
                  />
                ) : (
                  <span
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-v2-accent/40 bg-v2-accent/[0.06] text-v2-accent"
                    aria-hidden
                  >
                    <IconPlay size={12} />
                  </span>
                )}
                <VoiceAvatar voice={current} size="md" />
                <span className="min-w-0 flex-1 leading-4">
                  <span className="block truncate text-v2-13 font-medium text-v2-fg">
                    {label}
                    {sel.status === 'default' && current && (
                      <span className="ml-1.5 font-normal text-v2-fg-3">
                        · {voiceLabel(current)}
                      </span>
                    )}
                  </span>
                  <span className="block truncate text-v2-12 text-v2-fg-3">
                    {current
                      ? current.traits.join(' · ')
                      : sel.assistant_count
                        ? `${sel.assistant_count} phone ${sel.assistant_count === 1 ? 'line' : 'lines'}`
                        : 'No active phone line'}
                  </span>
                </span>
                <SavedNote when={savedAt}>{savedText}</SavedNote>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setOpen(true)}
                >
                  {canEdit ? 'Change' : 'Browse'}
                </Button>
              </div>
              {!canEdit && (
                <Reason className="mt-1.5">
                  {sel.status === 'unavailable'
                    ? 'Assign an assistant to this store to choose a voice.'
                    : 'Your role can browse voices but not change them.'}
                </Reason>
              )}
              <VoicePicker
                open={open}
                onClose={() => {
                  preview.stop();
                  setOpen(false);
                }}
                selection={sel}
                canEdit={canEdit}
                preview={preview}
                onSaved={(voiceId) => {
                  setSavedText(
                    voiceId
                      ? 'Voice updated for every active phone line.'
                      : 'Agent default voice selected.',
                  );
                  markSaved();
                }}
                voices={voices}
                catalogError={
                  sel.catalog_available === false
                    ? 'Voice choices are temporarily unavailable.'
                    : catalog.error
                }
              />
            </>
          );
        }}
      </QueryGate>
    </Block>
  );
};

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const VoicePicker: React.FC<{
  open: boolean;
  onClose: () => void;
  selection: VoiceSelection;
  canEdit: boolean;
  preview: ReturnType<typeof useAudioPreview>;
  onSaved: (voiceId: string | null) => void;
  voices: VoiceOption[];
  catalogError: string | null;
}> = ({
  open,
  onClose,
  selection,
  canEdit,
  preview,
  onSaved,
  voices,
  catalogError,
}) => {
  const voiceActions = useVoiceActions();
  const [draftId, setDraftId] = useState<string | null>(selection.voice_id);
  const [search, setSearch] = useState('');
  const [gender, setGender] = useState('');
  const [accent, setAccent] = useState('');
  const [type, setType] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDraftId(selection.voice_id);
      setError(null);
    }
  }, [open, selection.voice_id]);

  const accents = useMemo(
    () =>
      Array.from(new Set(voices.map((v) => v.accent).filter(Boolean))).sort(),
    [voices],
  );
  const types = useMemo(
    () =>
      Array.from(
        new Set(voices.map((v) => v.voice_type).filter(Boolean)),
      ).sort(),
    [voices],
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return voices.filter(
      (v) =>
        (!gender || v.gender === gender) &&
        (!accent || v.accent === accent) &&
        (!type || v.voice_type === type) &&
        (!q || voiceSearchText(v).includes(q)),
    );
  }, [accent, gender, search, type, voices]);
  const filtering = Boolean(search || gender || accent || type);
  const unchanged = draftId === selection.voice_id;
  const saveReason = !canEdit
    ? "The voice can't be changed right now."
    : unchanged
      ? 'Pick a different voice to save.'
      : undefined;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await voiceActions.save(draftId);
      onSaved(draftId);
      onClose();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Could not update the store voice.',
      );
    } finally {
      setSaving(false);
    }
  };

  const choose = (id: string | null) => {
    if (!canEdit) return;
    setDraftId(id);
    setError(null);
  };

  // A render function, not a component: rows must not remount while filtering.
  const optionRow = ({
    id,
    voice,
    title,
    sub,
    recommended,
  }: {
    id: string | null;
    voice: VoiceOption | null;
    title: string;
    sub?: string;
    recommended?: boolean;
  }) => {
    const selected = draftId === id;
    return (
      <div
        key={id ?? '__default'}
        role="radio"
        aria-checked={selected}
        aria-disabled={!canEdit || undefined}
        tabIndex={0}
        onClick={() => choose(id)}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            choose(id);
          }
        }}
        className={clsx(
          'flex h-11 items-center gap-3 border-b border-v2-line/70 px-2 outline-none transition-colors last:border-b-0',
          canEdit ? 'cursor-pointer' : 'cursor-default',
          selected
            ? 'bg-v2-accent/[0.07]'
            : canEdit && 'hover:bg-v2-sunken/70 focus-visible:bg-v2-sunken',
        )}
      >
        <span
          aria-hidden
          className={clsx(
            'inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-[1.5px]',
            selected ? 'border-v2-accent' : 'border-v2-line-strong',
          )}
        >
          {selected && (
            <span className="h-1.5 w-1.5 rounded-full bg-v2-accent" />
          )}
        </span>
        <VoiceAvatar voice={voice} />
        <span className="min-w-0 flex-1 leading-4">
          <span className="flex items-center gap-2 truncate text-v2-13 font-medium text-v2-fg">
            {title}
            {recommended && <Tag tone="accent">Recommended</Tag>}
          </span>
          {sub && (
            <span className="block truncate text-v2-12 text-v2-fg-3">
              {sub}
            </span>
          )}
        </span>
        {voice && (
          <PlayButton
            voice={voice}
            playing={preview.playingId === voice.voice_id}
            onToggle={() => preview.toggle(voice)}
          />
        )}
      </div>
    );
  };

  const agentDefault =
    voices.find((v) => v.voice_id === selection.agent_voice_id) ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Choose a voice"
      description="Play a sample, then save it for every active phone line."
      size="lg"
      footer={
        <>
          {error && (
            <Callout tone="danger" className="mr-auto py-1.5">
              {error}
            </Callout>
          )}
          {!error && saveReason && (
            <Reason className="mr-auto">{saveReason}</Reason>
          )}
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={save}
            loading={saving}
            disabledReason={saveReason}
          >
            Save voice
          </Button>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <Input
          size="sm"
          aria-label="Search voices"
          placeholder="Search by name or trait"
          leading={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="min-w-[180px] flex-1"
          data-autofocus="true"
        />
        <Select
          size="sm"
          aria-label="Gender"
          value={gender}
          onChange={(e) => setGender(e.target.value)}
          className="w-[112px]"
        >
          <option value="">Any gender</option>
          <option value="female">Female</option>
          <option value="male">Male</option>
        </Select>
        <Select
          size="sm"
          aria-label="Accent"
          value={accent}
          onChange={(e) => setAccent(e.target.value)}
          className="w-[140px]"
        >
          <option value="">Any accent</option>
          {accents.map((a) => (
            <option key={a} value={a}>
              {cap(a)}
            </option>
          ))}
        </Select>
        {types.length > 0 && (
          <Select
            size="sm"
            aria-label="Type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-[104px]"
          >
            <option value="">Any type</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {cap(t)}
              </option>
            ))}
          </Select>
        )}
      </div>
      <div
        role="radiogroup"
        aria-label="Voice"
        className="v2-scroll mt-3 max-h-[min(56vh,480px)] overflow-y-auto border-t border-v2-line"
      >
        {!filtering &&
          optionRow({
            id: null,
            voice: agentDefault,
            title: 'Agent default',
            sub: agentDefault
              ? `Currently ${voiceLabel(agentDefault)} — follows AskArthur's default when it changes`
              : "Follows AskArthur's default voice",
          })}
        {filtered.map((v) =>
          optionRow({
            id: v.voice_id,
            voice: v,
            title: voiceLabel(v),
            sub: v.traits.join(' · '),
            recommended: v.recommended,
          }),
        )}
        {catalogError && (
          <Callout tone="warning" className="mt-3">
            {catalogError} You can still keep the agent default.
          </Callout>
        )}
        {!catalogError && filtered.length === 0 && (
          <EmptyState
            compact
            title="No voices match these filters"
            description="Try a broader accent or type."
            action={
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setSearch('');
                  setGender('');
                  setAccent('');
                  setType('');
                }}
              >
                Clear filters
              </Button>
            }
          />
        )}
      </div>
      <p className="v2-num mt-2 text-v2-12 text-v2-fg-3">
        {filtered.length} of {voices.length} voices
        {selection.assistant_count > 0
          ? ` · applies to ${selection.assistant_count} phone ${selection.assistant_count === 1 ? 'line' : 'lines'}`
          : ''}
      </p>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Call intro
// ---------------------------------------------------------------------------

/**
 * Returns the block and its footer (SaveBar + confirm) separately so the
 * SaveBar can span the full column below the capped body.
 */
function useCallIntro(): { block: React.ReactNode; footer: React.ReactNode } {
  const info = useStoreInfoQuery('Call intro settings could not be loaded.');
  const storeActions = useStoreActions();
  const [useDefault, setUseDefault] = useState(true);
  const [sentence, setSentence] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [savedAt, markSaved] = useSavedAt();

  const saved = info.data;
  useEffect(() => {
    if (!saved) return;
    setUseDefault(saved.use_default_call_intro_sentence ?? true);
    setSentence(saved.call_intro_sentence ?? '');
    setError(null);
  }, [saved]);

  const defaultIntro =
    saved?.default_call_intro_sentence ||
    defaultCallIntroSentence(
      saved?.phonetic_name || saved?.name || saved?.store_name,
    );
  const dirty =
    !!saved &&
    (useDefault !== (saved.use_default_call_intro_sentence ?? true) ||
      (!useDefault && sentence !== (saved.call_intro_sentence ?? '')));
  const customEmpty = !useDefault && !sentence.trim();

  const persist = async () => {
    setSaving(true);
    setError(null);
    try {
      await storeActions.update({
        call_intro_sentence: sentence,
        use_default_call_intro_sentence: useDefault,
      });
      markSaved();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Could not save the call intro.',
      );
    } finally {
      setSaving(false);
      setConfirming(false);
    }
  };

  const onSave = () => {
    if (customEmpty) {
      setError(
        'Write the intro Arthur should say, or switch back to the default.',
      );
      return;
    }
    if (useDefault) void persist();
    else setConfirming(true);
  };

  const block = (
    <Block title="Call intro" control={<SavedNote when={savedAt} />}>
      <QueryGate query={info} skeleton={<BlockSkeleton rows={3} />}>
        {() => (
          <div
            role="radiogroup"
            aria-label="Call intro"
            className="flex max-w-[640px] flex-col gap-3"
          >
            <Radio
              name="call-intro"
              label="Default intro"
              description={
                <span className="text-v2-fg-2">“{defaultIntro}”</span>
              }
              checked={useDefault}
              onChange={() => {
                setUseDefault(true);
                setError(null);
              }}
            />
            <Radio
              name="call-intro"
              label="Custom intro"
              description="Write your own opening line. It replaces the default on every call."
              checked={!useDefault}
              onChange={() => {
                setUseDefault(false);
                setError(null);
              }}
            />
            {!useDefault && (
              <div className="ml-6 flex flex-col gap-1.5">
                <Textarea
                  aria-label="Custom intro"
                  rows={3}
                  value={sentence}
                  invalid={customEmpty && !!error}
                  placeholder="Thanks for calling, how can I help?"
                  onChange={(e) => {
                    setSentence(e.target.value);
                    setError(null);
                  }}
                  data-autofocus="true"
                />
                <Reason>
                  Names that are hard to pronounce can be spelled the way they
                  sound.
                </Reason>
              </div>
            )}
          </div>
        )}
      </QueryGate>
    </Block>
  );

  const footer = (
    <>
      <SaveBar
        dirty={dirty}
        saving={saving}
        error={error}
        onSave={onSave}
        onDiscard={() => {
          if (!saved) return;
          setUseDefault(saved.use_default_call_intro_sentence ?? true);
          setSentence(saved.call_intro_sentence ?? '');
          setError(null);
        }}
        summary="Call intro changed"
      />
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={persist}
        title="Review your custom intro"
        tone="primary"
        confirmLabel="Save custom intro"
        busy={saving}
        consequence="Every caller hears this sentence first. After saving, call the store yourself to check that names sound right."
      >
        <Callout tone="neutral">“{sentence.trim()}”</Callout>
      </ConfirmDialog>
    </>
  );

  return { block, footer };
}

export default VoiceSection;
